import { useState, useEffect } from 'react';
import { backdropClose } from '../../utils/backdropClose';
import toast from 'react-hot-toast';
import { Plus, Trash2, Settings, X, HelpCircle } from 'lucide-react';

interface CatalogTemplate {
    id: string;
    provider: string;
    triggerEvent: string;
    title: string;
    templateName: string;
    providerTemplateName: string | null;
    content: string;
    language: string;
    category: string | null;
    pricePerMessage: number | null;
    parameterCount: number;
    type: string;
    isSchedulable: boolean;
    defaultEnabled: boolean;
    isActive: boolean;
    headerType: string | null;
}

interface Gym {
    id: string;
    name: string;
    displayName: string;
}

interface GymAssignment {
    templateId: string;
    gymId: string;
    gymName: string;
    isEnabled: boolean;
}

interface Props {
    token: string;
}

const getApiBaseUrl = () => {
    const url = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';
    return url.endsWith('/') ? url.slice(0, -1) : url;
};
const API_BASE_URL = getApiBaseUrl();

const PROVIDERS = ['meta', 'whatchimp', 'aisensy'];
const CATEGORIES = ['utility', 'marketing', 'authentication'];

// Trigger events the system actually fires. "Event" triggers send in real time when
// the action happens; "Scheduled" triggers are run by the scheduler. Keys must match
// exactly (they are case-sensitive) so the send pipeline can find the template.
const TRIGGER_EVENTS: { key: string; type: 'Event' | 'Scheduled'; description: string }[] = [
    { key: 'ACCOUNT_CREATED', type: 'Event', description: 'A new member account is created — e.g. welcome message.' },
    { key: 'MEMBERSHIP_PURCHASED', type: 'Event', description: 'A member buys or renews a membership plan.' },
    { key: 'payment_received', type: 'Event', description: 'A payment is successfully recorded for a member. Mutually exclusive with payment_received_with_file — enabling one disables the other for that gym.' },
    { key: 'payment_received_with_file', type: 'Event', description: 'Same trigger as payment_received, but attaches the purchase invoice PDF. Mutually exclusive with payment_received. Requires a Header type of "document".' },
    { key: 'invoice_manual_send', type: 'Event', description: 'Staff clicks "Send Invoice" on a purchase — sends the invoice PDF on demand. Requires a Header type of "document".' },
    { key: 'TRIAL_STARTED', type: 'Event', description: "A member's free trial begins." },
    { key: 'LEAD_CREATED', type: 'Event', description: 'A prospective member (lead) is captured.' },
    { key: 'account_otp', type: 'Event', description: 'A one-time password is sent for member login / verification.' },
    { key: 'CLASS_BOOKING_CONFIRMED', type: 'Event', description: "A member's class booking is confirmed." },
    { key: 'CLASS_CANCELLED', type: 'Event', description: 'A scheduled class is cancelled — notifies the booked members.' },
    { key: 'WAITLIST_PROMOTED', type: 'Event', description: 'A waitlisted member is promoted into a freed-up class slot.' },
    { key: 'payment_reminder', type: 'Scheduled', description: 'Sent ~2 days before a membership expires, only if no renewal is already booked. Runs on the scheduler.' },
];

const emptyForm = {
    provider: 'whatchimp',
    triggerEvent: '',
    title: '',
    templateName: '',
    providerTemplateName: '',
    content: '',
    language: 'en',
    category: 'utility',
    pricePerMessage: '',
    parameterCount: 0,
    type: 'event',
    isSchedulable: false,
    headerType: ''
};

export default function CatalogTemplatesTab({ token }: Props) {
    const [templates, setTemplates] = useState<CatalogTemplate[]>([]);
    const [gyms, setGyms] = useState<Gym[]>([]);
    const [loading, setLoading] = useState(true);
    const [providerFilter, setProviderFilter] = useState<string>('');
    const [showForm, setShowForm] = useState(false);
    const [editing, setEditing] = useState<CatalogTemplate | null>(null);
    const [form, setForm] = useState<any>(emptyForm);
    const [submitting, setSubmitting] = useState(false);

    // Per-gym enablement panel
    const [managing, setManaging] = useState<CatalogTemplate | null>(null);
    const [assignments, setAssignments] = useState<GymAssignment[]>([]);
    const [selectedGym, setSelectedGym] = useState('');

    // Trigger-event help modal
    const [showTriggerHelp, setShowTriggerHelp] = useState(false);

    const authHeaders = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

    useEffect(() => { fetchData(); }, [token, providerFilter]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [tplRes, gymRes] = await Promise.all([
                fetch(`${API_BASE_URL}/api/saas/catalog-templates${providerFilter ? `?provider=${providerFilter}` : ''}`, { headers: authHeaders }),
                fetch(`${API_BASE_URL}/api/saas/gyms/list`, { headers: authHeaders })
            ]);
            const tplData = await tplRes.json();
            const gymData = await gymRes.json();
            setTemplates(tplData.data || []);
            setGyms(gymData.data || []);
        } catch (e) {
            toast.error('Failed to load catalog');
        } finally {
            setLoading(false);
        }
    };

    const openCreate = () => { setEditing(null); setForm(emptyForm); setShowForm(true); };
    const openEdit = (t: CatalogTemplate) => {
        setEditing(t);
        setForm({
            provider: t.provider,
            triggerEvent: t.triggerEvent,
            title: t.title,
            templateName: t.templateName,
            providerTemplateName: t.providerTemplateName || '',
            content: t.content,
            language: t.language,
            category: t.category || 'utility',
            pricePerMessage: t.pricePerMessage != null ? String(t.pricePerMessage) : '',
            parameterCount: t.parameterCount,
            type: t.type,
            isSchedulable: t.isSchedulable,
            headerType: t.headerType || ''
        });
        setShowForm(true);
    };

    const submitForm = async () => {
        if (!form.triggerEvent || !form.title || !form.templateName || !form.content) {
            toast.error('Trigger event, title, template name and content are required');
            return;
        }
        // The send pipeline only falls back to a default param mapping when it resolves
        // FEWER params than parameterCount declares — a parameterCount that's too LOW
        // (e.g. left at 0 while the body has {{1}}/{{2}}) sails through silently and
        // Meta rejects the send at #132000 instead. Catch that here before it ships.
        const highestPlaceholder = Math.max(0, ...Array.from(form.content.matchAll(/\{\{(\d+)\}\}/g), (m: any) => Number(m[1])));
        const declaredCount = Number(form.parameterCount) || 0;
        if (highestPlaceholder > declaredCount) {
            toast.error(`Content uses {{${highestPlaceholder}}} but Parameter count is only ${declaredCount} — raise it to at least ${highestPlaceholder}.`);
            return;
        }
        try {
            setSubmitting(true);
            const body = {
                ...form,
                pricePerMessage: form.pricePerMessage === '' ? null : Number(form.pricePerMessage),
                parameterCount: Number(form.parameterCount) || 0,
                headerType: form.headerType === '' ? null : form.headerType
            };
            const url = editing
                ? `${API_BASE_URL}/api/saas/catalog-templates/${editing.id}`
                : `${API_BASE_URL}/api/saas/catalog-templates`;
            const res = await fetch(url, { method: editing ? 'PUT' : 'POST', headers: authHeaders, body: JSON.stringify(body) });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Save failed');
            toast.success(editing ? 'Template updated' : 'Template onboarded');
            setShowForm(false);
            fetchData();
        } catch (e: any) {
            toast.error(e.message || 'Save failed');
        } finally {
            setSubmitting(false);
        }
    };

    const retire = async (t: CatalogTemplate) => {
        if (!confirm(`Retire "${t.title}"? Existing gym assignments keep working.`)) return;
        try {
            const res = await fetch(`${API_BASE_URL}/api/saas/catalog-templates/${t.id}`, { method: 'DELETE', headers: authHeaders });
            if (!res.ok) throw new Error();
            toast.success('Template retired');
            fetchData();
        } catch { toast.error('Failed to retire'); }
    };

    const openManage = async (t: CatalogTemplate) => {
        setManaging(t);
        setSelectedGym('');
        await loadAssignments(t.id);
    };

    const loadAssignments = async (templateId: string) => {
        try {
            const res = await fetch(`${API_BASE_URL}/api/saas/catalog-templates/${templateId}/gyms`, { headers: authHeaders });
            const data = await res.json();
            setAssignments(data.data || []);
        } catch { toast.error('Failed to load gym assignments'); }
    };

    const enableForGym = async () => {
        if (!managing || !selectedGym) { toast.error('Select a gym'); return; }
        try {
            const res = await fetch(`${API_BASE_URL}/api/saas/catalog-templates/${managing.id}/gyms`, {
                method: 'POST', headers: authHeaders, body: JSON.stringify({ gymId: selectedGym })
            });
            if (!res.ok) { const d = await res.json(); throw new Error(d.error); }
            toast.success('Enabled for gym');
            setSelectedGym('');
            loadAssignments(managing.id);
        } catch (e: any) { toast.error(e.message || 'Failed'); }
    };

    const toggleAssignment = async (a: GymAssignment) => {
        try {
            const res = await fetch(`${API_BASE_URL}/api/saas/templates/${a.templateId}/enabled`, {
                method: 'PATCH', headers: authHeaders, body: JSON.stringify({ isEnabled: !a.isEnabled })
            });
            if (!res.ok) throw new Error();
            if (managing) loadAssignments(managing.id);
        } catch { toast.error('Failed to toggle'); }
    };

    const availableGyms = gyms.filter(g => !assignments.some(a => a.gymId === g.id));

    return (
        <div>
            <div className="flex items-center justify-between mb-4">
                <div>
                    <h3 className="text-lg font-semibold text-gray-900">Template Catalog</h3>
                    <p className="text-sm text-gray-500">Onboard partner templates, set type &amp; price, and enable per gym.</p>
                </div>
                <div className="flex items-center gap-2">
                    <select value={providerFilter} onChange={e => setProviderFilter(e.target.value)} className="border rounded-md px-2 py-1.5 text-sm">
                        <option value="">All providers</option>
                        {PROVIDERS.map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                    <button onClick={openCreate} className="inline-flex items-center gap-1 bg-indigo-600 text-white text-sm px-3 py-1.5 rounded-md hover:bg-indigo-700">
                        <Plus size={16} /> Onboard Template
                    </button>
                </div>
            </div>

            {loading ? (
                <p className="text-sm text-gray-500">Loading…</p>
            ) : templates.length === 0 ? (
                <p className="text-sm text-gray-500">No catalog templates yet. Click "Onboard Template" to add one.</p>
            ) : (
                <div className="overflow-x-auto border rounded-lg">
                    <table className="min-w-full text-sm">
                        <thead className="bg-gray-50 text-gray-600">
                            <tr>
                                <th className="text-left px-3 py-2">Template</th>
                                <th className="text-left px-3 py-2">Provider</th>
                                <th className="text-left px-3 py-2">Type</th>
                                <th className="text-left px-3 py-2">Price/msg</th>
                                <th className="text-left px-3 py-2">Trigger</th>
                                <th className="text-left px-3 py-2">Status</th>
                                <th className="text-right px-3 py-2">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y">
                            {templates.map(t => (
                                <tr key={t.id} className={t.isActive ? '' : 'opacity-50'}>
                                    <td className="px-3 py-2">
                                        <div className="font-medium text-gray-900">{t.title}</div>
                                        <div className="text-xs text-gray-500">{t.templateName}</div>
                                    </td>
                                    <td className="px-3 py-2 capitalize">{t.provider}</td>
                                    <td className="px-3 py-2">
                                        {t.category ? (
                                            <span className="inline-block px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-700 capitalize">{t.category}</span>
                                        ) : <span className="text-gray-400">—</span>}
                                    </td>
                                    <td className="px-3 py-2">{t.pricePerMessage != null ? `₹${t.pricePerMessage}` : <span className="text-gray-400">flat</span>}</td>
                                    <td className="px-3 py-2 text-gray-600">{t.triggerEvent}</td>
                                    <td className="px-3 py-2">{t.isActive ? <span className="text-green-600">Active</span> : <span className="text-gray-500">Retired</span>}</td>
                                    <td className="px-3 py-2">
                                        <div className="flex items-center justify-end gap-2">
                                            <button onClick={() => openManage(t)} title="Enable per gym" className="text-gray-600 hover:text-indigo-600"><Settings size={16} /></button>
                                            <button onClick={() => openEdit(t)} className="text-indigo-600 hover:underline text-xs">Edit</button>
                                            <button onClick={() => retire(t)} title="Retire" className="text-red-500 hover:text-red-700"><Trash2 size={16} /></button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Create / edit modal */}
            {showForm && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h4 className="text-lg font-semibold">{editing ? 'Edit Template' : 'Onboard Template'}</h4>
                            <button onClick={() => setShowForm(false)}><X size={18} /></button>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <label className="text-sm">Provider
                                <select disabled={!!editing} value={form.provider} onChange={e => setForm({ ...form, provider: e.target.value })} className="mt-1 w-full border rounded-md px-2 py-1.5">
                                    {PROVIDERS.map(p => <option key={p} value={p}>{p}</option>)}
                                </select>
                            </label>
                            <div className="text-sm">
                                <div className="flex items-center gap-1">
                                    <span>Trigger event</span>
                                    <button type="button" onClick={() => setShowTriggerHelp(true)} title="View available trigger events" className="text-gray-400 hover:text-indigo-600">
                                        <HelpCircle size={14} />
                                    </button>
                                </div>
                                <input value={form.triggerEvent} onChange={e => setForm({ ...form, triggerEvent: e.target.value })} placeholder="e.g. payment_reminder" className="mt-1 w-full border rounded-md px-2 py-1.5" />
                            </div>
                            <label className="text-sm">Title
                                <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className="mt-1 w-full border rounded-md px-2 py-1.5" />
                            </label>
                            <label className="text-sm">Template name (internal)
                                <input value={form.templateName} onChange={e => setForm({ ...form, templateName: e.target.value })} className="mt-1 w-full border rounded-md px-2 py-1.5" />
                            </label>
                            <label className="text-sm">Provider template name
                                <input value={form.providerTemplateName} onChange={e => setForm({ ...form, providerTemplateName: e.target.value })} placeholder="name registered with partner" className="mt-1 w-full border rounded-md px-2 py-1.5" />
                            </label>
                            <label className="text-sm">Language
                                <input value={form.language} onChange={e => setForm({ ...form, language: e.target.value })} className="mt-1 w-full border rounded-md px-2 py-1.5" />
                            </label>
                            <label className="text-sm">Type (category)
                                <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} className="mt-1 w-full border rounded-md px-2 py-1.5 capitalize">
                                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                                </select>
                            </label>
                            <label className="text-sm">Price per message (₹)
                                <input type="number" step="0.0001" min="0" value={form.pricePerMessage} onChange={e => setForm({ ...form, pricePerMessage: e.target.value })} placeholder="blank = gym flat rate" className="mt-1 w-full border rounded-md px-2 py-1.5" />
                            </label>
                            <label className="text-sm">Parameter count
                                <input type="number" min="0" value={form.parameterCount} onChange={e => setForm({ ...form, parameterCount: e.target.value })} className="mt-1 w-full border rounded-md px-2 py-1.5" />
                            </label>
                            <label className="text-sm">Header type
                                <select value={form.headerType} onChange={e => setForm({ ...form, headerType: e.target.value })} className="mt-1 w-full border rounded-md px-2 py-1.5">
                                    <option value="">None</option>
                                    <option value="document">Document (attaches a PDF — requires a Meta-approved template with a DOCUMENT header)</option>
                                </select>
                            </label>
                            <label className="text-sm">Send type
                                <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })} className="mt-1 w-full border rounded-md px-2 py-1.5">
                                    <option value="event">event</option>
                                    <option value="schedule">schedule</option>
                                    <option value="manual">manual (campaign)</option>
                                </select>
                            </label>
                            <label className="text-sm col-span-2 flex items-center gap-2">
                                <input type="checkbox" checked={form.isSchedulable} onChange={e => setForm({ ...form, isSchedulable: e.target.checked })} />
                                Schedulable (can be run by the scheduler)
                            </label>
                            <label className="text-sm col-span-2">Content
                                <textarea value={form.content} onChange={e => setForm({ ...form, content: e.target.value })} rows={4} placeholder="Use {{1}}, {{2}} for variables" className="mt-1 w-full border rounded-md px-2 py-1.5" />
                            </label>
                        </div>
                        <div className="flex justify-end gap-2 mt-4">
                            <button onClick={() => setShowForm(false)} className="px-3 py-1.5 text-sm border rounded-md">Cancel</button>
                            <button onClick={submitForm} disabled={submitting} className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-md disabled:opacity-50">{submitting ? 'Saving…' : 'Save'}</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Per-gym enablement modal */}
            {managing && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h4 className="text-lg font-semibold">Enable "{managing.title}" per gym</h4>
                            <button onClick={() => setManaging(null)}><X size={18} /></button>
                        </div>
                        <div className="flex items-center gap-2 mb-4">
                            <select value={selectedGym} onChange={e => setSelectedGym(e.target.value)} className="flex-1 border rounded-md px-2 py-1.5 text-sm">
                                <option value="">Select a gym…</option>
                                {availableGyms.map(g => <option key={g.id} value={g.id}>{g.displayName || g.name}</option>)}
                            </select>
                            <button onClick={enableForGym} className="bg-indigo-600 text-white text-sm px-3 py-1.5 rounded-md">Enable</button>
                        </div>
                        {assignments.length === 0 ? (
                            <p className="text-sm text-gray-500">Not enabled for any gym yet.</p>
                        ) : (
                            <ul className="divide-y border rounded-md">
                                {assignments.map(a => (
                                    <li key={a.templateId} className="flex items-center justify-between px-3 py-2 text-sm">
                                        <span>{a.gymName}</span>
                                        <button onClick={() => toggleAssignment(a)} className={`px-2 py-0.5 rounded-full text-xs ${a.isEnabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                                            {a.isEnabled ? 'Enabled' : 'Disabled'}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            )}

            {/* Trigger-event help modal (opens above the onboard/edit modal) */}
            {showTriggerHelp && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60] p-4" {...backdropClose(() => setShowTriggerHelp(false))}>
                    <div className="bg-white rounded-lg w-full max-w-2xl max-h-[85vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-1">
                            <h4 className="text-lg font-semibold">Available Trigger Events</h4>
                            <button onClick={() => setShowTriggerHelp(false)}><X size={18} /></button>
                        </div>
                        <p className="text-sm text-gray-500 mb-4">
                            The trigger event decides when this template is sent. Keys are <strong>case-sensitive</strong> and must match exactly. Click a row to fill it in.
                        </p>
                        <div className="overflow-x-auto border rounded-lg">
                            <table className="min-w-full text-sm">
                                <thead className="bg-gray-50 text-gray-600">
                                    <tr>
                                        <th className="text-left px-3 py-2">Event key</th>
                                        <th className="text-left px-3 py-2">When it fires</th>
                                        <th className="text-left px-3 py-2">Type</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y">
                                    {TRIGGER_EVENTS.map(ev => (
                                        <tr
                                            key={ev.key}
                                            className="hover:bg-indigo-50 cursor-pointer"
                                            onClick={() => { setForm((f: any) => ({ ...f, triggerEvent: ev.key })); setShowTriggerHelp(false); }}
                                        >
                                            <td className="px-3 py-2 font-mono text-xs text-gray-900 whitespace-nowrap">{ev.key}</td>
                                            <td className="px-3 py-2 text-gray-600">{ev.description}</td>
                                            <td className="px-3 py-2">
                                                <span className={`inline-block px-2 py-0.5 rounded-full text-xs ${ev.type === 'Scheduled' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}`}>{ev.type}</span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <p className="text-xs text-gray-400 mt-3">
                            Event triggers send in real time when the action happens. Scheduled triggers are run by the messaging scheduler.
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
}
